import './orderItems.css';

import {
  IncomingItemGroup,
  Order,
  OutgoingItemGroup,
  RefundModel,
  Ticket,
  colorForOrderStatus,
  loadOrderWithItemGroups,
  useTicketUrlPollUntilAllHaveUrl,
} from '../../../../model/order';
import { ProductVariant, getProductName } from '../../../../model/product';
import React, { useState } from 'react';
import {
  Selection,
  formatCurrency,
  formatPercentage,
  generateMailToLink,
  presence,
  useSelection,
} from '../../../../util/util';
import { deleteItems, refundItems, removeItems } from '../../../../model/item';
import { isEqual, uniqWith } from 'lodash';

import { AddItemsForm } from '../modals/AddItemsForm/AddItemsForm';
import { Card } from '../../../../components/Card/Card';
import { ControlledModal } from '../../../../components/Modal/ControlledModal';
import { Delivery } from '../../../../model/delivery';
import { DocumentCell } from './OrderItemsRow/Documents';
import { EditDocumentForm } from '../modals/EditDocumentForm';
import { Icon } from '../../../../components/Icon/Icon';
import { Invoice } from '../../../../model/invoice';
import { ItemsSubList } from './ItemsSubList';
import { Modal } from '../../../../components/Modal/Modal';
import { PriceCell } from './OrderItemsRow/Price';
import { ProductCell } from './OrderItemsRow/ProductCell';
import { RefundModal } from '../modals/RefundModal';
import { RemoveItemsModal } from '../modals/RemoveItemsModal';
import { SortableList } from '../../../../components/SortableList/SortableList';
import { Tag } from '../../../../components/Tag/Tag';
import { VariantCell } from './OrderItemsRow/Variant';
import { camelCase } from 'lodash';
import classnames from 'classnames';
import { compact } from 'lodash';
import { getItemStatus } from './OrderUtils';
import { handleError } from '../../../../model/notification';
import { t } from '../../../../locales/translate';
import { Link } from 'react-router-dom';
import { genericProductPath } from '../../../../routes';
import { Stack } from '../../../../components/Stack/Stack';
import { useModalPresenter } from '../../../../components/Modal/PresentedModal';
import { OfferForResale } from '../modals/offer_for_resale';

function getProductDisplayName(row: OutgoingItemGroup | IncomingItemGroup, order: Order): JSX.Element {
  let prefill: React.ReactNode | undefined;
  let variant: Partial<ProductVariant> | undefined;
  // If the row is the original item display it's name
  let productName = getProductName(row.product);

  // If the row is a return, then we display the name of the returned item
  const invalidateItem = order.itemGroups?.find((i) => i.id === row.invalidatingIds?.[0]);
  if (invalidateItem) {
    prefill = <strong>{t.general.return}: </strong>;
    variant = invalidateItem?.productVariant;
    productName = getProductName(invalidateItem?.product);
  }

  // If the row is a refund, then we display the name of the refunded item
  const refundItem = order.itemGroups?.find((i) => i.id === row.refundingIds?.[0]);
  if (refundItem) {
    prefill = <strong>{t.general.refund}: </strong>;
    variant = refundItem?.productVariant;
    productName = getProductName(refundItem?.product);
  }

  const displayName = (
    <div>
      {prefill}
      <span>{productName}</span>
      {[variant?.sku, variant?.description].filter(Boolean).map((i) => `- ${i}`)}
    </div>
  );

  return displayName;
}

interface Props {
  anchorId?: string;
  order: Order;
  onChange: () => void;
}

export function OrderItems({ anchorId, order, onChange }: Props): JSX.Element {
  const [modal, setModal] = useState({
    refund: false,
    addProduct: false,
    personalIdentifier: false,
  });
  const selection = useSelection<string>();

  const selectedGroups = (order.itemGroups ?? []).filter((group) => selection.hasEvery(group.itemIds));
  const partiallySelectedGroups = (order.itemGroups ?? []).filter((group) => selection.hasSome(group.itemIds));
  const [documentEdit, setDocumentEdit] = useState<
    | {
        item: OutgoingItemGroup | IncomingItemGroup;
        document?: Invoice | Delivery;
        documentType: 'delivery' | 'invoice';
        itemIds: string[];
      }
    | undefined
  >(undefined);

  const ticketUrlPolling = useTicketUrlPollUntilAllHaveUrl(order);

  const invoiceAddresses = uniqWith(
    order.invoices.map((inv) => inv.address),
    isEqual
  );

  const deliveryAddresses = uniqWith(
    order.deliveries.map((del) => del.address),
    isEqual
  );

  function highlightItems(row?: OutgoingItemGroup | IncomingItemGroup): void {
    if (!row) {
      const items = document.querySelectorAll('.order-details__items');
      items.forEach((node) => node.classList.remove('order-details__highlighted-row'));
    } else {
      const ids = [
        ...(row.invalidatedByIds || []),
        ...(row.refundedByIds || []),
        ...(row.invalidatingIds || []),
        ...(row.refundingIds || []),
      ];
      let items: Element[] = [];
      ids.forEach((id) => {
        items = [...items, ...(document.querySelectorAll(`.id-${id}`) as unknown as Element[])];
      });
      items.forEach((node) => node.classList.add('order-details__highlighted-row'));
    }
  }

  /* SortableList takes only known keys as columns which makes it difficult to work with two different types. Outgoing Item Group has many
  properties that Incoming Item Group does not. */
  const items = (order.itemGroups || []) as Array<OutgoingItemGroup | IncomingItemGroup>;

  return (
    <>
      <Card
        anchorId={anchorId}
        title={
          selection.size === 0
            ? t.model.item._plural
            : `${selection.size} ${t.general.selected} ${
                selection.size === 1 ? t.model.item._name : t.model.item._plural
              }`
        }
        className={classnames({
          'order-details__items--selected': selection.size > 0,
        })}
        actions={
          <Actions
            groups={partiallySelectedGroups}
            selection={selection}
            onSetModal={(type) => {
              setModal({ ...modal, [type]: true });
            }}
            order={order}
            onChange={onChange}
          />
        }
        elevation={2}
        fullbleed
      >
        <SortableList<OutgoingItemGroup | IncomingItemGroup>
          items={items.map((item) => ({
            ...item,
            className: `order-details__items id-${item.id} product-sku-${item.product?.sku}`,
          }))}
          multiSelect
          selectedItems={
            selectedGroups.map((item) => ({
              ...item,
              className: `order-details__items id-${item.id}`,
            })) as unknown as typeof items
          }
          onRowSelect={(row) =>
            selection.hasEvery(row.itemIds) ? selection.remove(row.itemIds) : selection.add(row.itemIds)
          }
          onSelectAll={() => {
            if (selectedGroups.length === items.length) {
              selection.clear();
            } else {
              selection.add(items.flatMap((e) => e.itemIds));
            }
          }}
          expandable={(row) => row.itemIds.length <= 150 && (row.itemIds.length > 1 || (row.tickets?.length ?? 0) > 0)}
          expandedContent={(row) => (
            <ItemsSubList
              order={order}
              group={row}
              selection={selection}
              isPollingTicketUrls={ticketUrlPolling.running}
            />
          )}
          emptyMessage={t.model.order.noItems}
          columns={[
            {
              dataKey: 'sku',
              label: t.model.product.sku,
              width: 100,
              cellRenderer: (row) =>
                row.product?.id && <Link to={genericProductPath(row.product)}>{row.product?.sku}</Link>,
            },
            {
              dataKey: 'productId',
              width: 350,
              label: t.model.product._name,
              cellRenderer: (row) => {
                return (
                  <div className="order-items__product-cell-wrapper">
                    <ProductCell
                      invoice={order.invoices.find((i) => i.id === row.invoiceId)}
                      onMouseEnter={() => {
                        highlightItems(row);
                      }}
                      onMouseLeave={() => {
                        highlightItems();
                      }}
                      product={row.product}
                      displayName={getProductDisplayName(row, order)}
                      invalidated={row.invalidated}
                      tickets={row.tickets}
                      isPollingTicketUrls={ticketUrlPolling.running}
                    />
                    {row.voucherCode && (
                      <p>
                        {row.voucherCode.voucherCampaign.title} - {row.voucherCode.code}
                      </p>
                    )}
                  </div>
                );
              },
            },
            {
              dataKey: 'productVariant',
              width: 30,
              label: t.model.variant._name,
              cellRenderer: (row) => (
                <>
                  <VariantCell isEvent={row.product?.__typename === 'Event'} variant={row.productVariant} />
                  {row.shop && row.shop.id !== order.shopId && (
                    <small className="order-details__shop-association">{row.shop?.name}</small>
                  )}
                  {!row.shop && !order.shopId && (
                    <small className="order-details__shop-association">{t.errors.noShopAssigned}</small>
                  )}
                </>
              ),
            },
            {
              dataKey: 'count',
              width: 30,
              label: t.model.item.quantity,
            },
            {
              dataKey: 'price',
              width: 30,
              label: t.model.price.grossPrice,
              cellRenderer: (row) =>
                row.invoiceId && (
                  <Stack size="zero">
                    <PriceCell
                      price={row.total}
                      title={`${formatCurrency(row.price)} + ${formatCurrency(row.vat)}`}
                      incoming={row.__typename === 'IncomingItemGroup'}
                    />
                    {!!row.discountAmount && <p>-{formatCurrency(row.discountAmount)}</p>}
                  </Stack>
                ),
            },
            {
              dataKey: 'vat',
              width: 50,
              label: 'vat',
              cellRenderer: (row) => row.invoiceId && row.vatPercentage && formatPercentage(row.vatPercentage),
            },
            {
              dataKey: 'invoiceId',
              width: 30,
              label: t.model.invoice._plural,
              cellRenderer: (row) => {
                const invoice = order.invoices.find((invoice) => invoice.id === row.invoiceId);
                if (invoice) {
                  return (
                    <DocumentCell
                      order={order}
                      document={invoice}
                      documentType="invoice"
                      onClick={() => {
                        setDocumentEdit({
                          item: row,
                          document: invoice,
                          documentType: 'invoice',
                          itemIds: row.itemIds ? row.itemIds : [],
                        });
                      }}
                      onChange={() => {
                        selection.clear();
                        onChange();
                      }}
                      displayContextMenu={row.permissions.changeInvoice}
                    />
                  );
                }
                if (row.__typename !== 'IncomingItemGroup' && row.permissions.changeInvoice) {
                  return (
                    <Icon
                      className="order-details__icon"
                      name="edit--sm"
                      onClick={() => {
                        setDocumentEdit({
                          item: row,
                          documentType: 'invoice',
                          itemIds: row.itemIds ? row.itemIds : [],
                        });
                      }}
                    />
                  );
                }
                return null;
              },
            },
            {
              dataKey: 'deliveryId',
              width: 30,
              label: t.model.order.deliveries,
              cellRenderer: (row) => {
                const delivery = order.deliveries.find((delivery) => delivery.id === row.deliveryId);
                if (delivery) {
                  return (
                    <DocumentCell
                      order={order}
                      document={delivery}
                      documentType="delivery"
                      incoming={row.__typename === 'IncomingItemGroup'}
                      onClick={() => {
                        setDocumentEdit({
                          item: row,
                          documentType: 'delivery',
                          document: delivery,
                          itemIds: row.itemIds || [],
                        });
                      }}
                      onChange={() => {
                        selection.clear();
                        onChange();
                      }}
                      displayContextMenu={row.permissions.changeDelivery || !!delivery.picklistId}
                    />
                  );
                } else if (row.permissions.changeDelivery) {
                  return (
                    <Icon
                      className="order-details__icon"
                      name="edit--sm"
                      onClick={() => {
                        setDocumentEdit({
                          item: row,
                          documentType: 'delivery',
                          itemIds: row.itemIds || [],
                        });
                      }}
                    />
                  );
                }
                return null;
              },
            },
            {
              dataKey: 'total',
              width: 50,
              label: t.model.status._name,
              cellRenderer: (row) => {
                const status = getItemStatus(order, row);

                return (
                  <Tag
                    size="small"
                    squared
                    text={t.model.status[camelCase(status)]}
                    backgroundColor={colorForOrderStatus(status)}
                  />
                );
              },
            },
          ]}
        />
      </Card>
      <Modal
        open={modal.addProduct}
        position="aside"
        size="small"
        title={t.actions.add(t.model.item._name)}
        overflow
        className="add-items-modal"
        scrollShadows={false}
      >
        <AddItemsForm
          order={order}
          preferedAddresses={{
            invoice: invoiceAddresses[0],
            delivery: deliveryAddresses[0],
          }}
          onClose={() => {
            setModal({
              ...modal,
              addProduct: false,
            });
          }}
          onChange={onChange}
        />
      </Modal>
      {/* EDIT DOCUMENT */}
      <Modal
        open={!!documentEdit}
        size="small"
        title={documentEdit ? t.actions.edit(t.model[documentEdit.documentType]._name) : ''}
      >
        <EditDocumentForm
          order={order}
          itemIds={documentEdit?.itemIds || []}
          preferedAddresses={invoiceAddresses[0]}
          document={documentEdit?.document}
          documentType={documentEdit?.documentType}
          item={documentEdit?.item}
          onClose={() => {
            setDocumentEdit(undefined);
          }}
        />
      </Modal>
      <ControlledModal<RefundModel>
        open={modal.refund}
        onClose={() => {
          setModal({ ...modal, refund: false });
          selection.clear();
        }}
        title={t.model.refund.refundItems}
        size="small"
        overflow={true}
      >
        {(controller) => (
          <RefundModal
            orderId={order.id}
            itemGroups={partiallySelectedGroups}
            itemIds={Array.from(selection.all)}
            controller={controller}
            onChange={onChange}
          />
        )}
      </ControlledModal>
    </>
  );
}

interface ActionsProps {
  groups: Array<OutgoingItemGroup | IncomingItemGroup>;
  selection: Selection<string>;
  order: Order;
  onSetModal: (modal: 'addProduct' | 'refund') => void;
  onChange: () => void;
}

async function shareTickets(order: Order, selectedOutgoingTickets: Ticket[]): Promise<void> {
  const emails = compact([order.user?.email].map(presence));
  const ticketUrls = compact(selectedOutgoingTickets.map((t) => t?.url)).join('\n');
  window.location.href = generateMailToLink({ email: emails, subject: '', body: ticketUrls });
}

function Actions({ groups, selection, order, onSetModal, onChange }: ActionsProps): JSX.Element {
  const modal = useModalPresenter();

  function areSelectedRefundable(): boolean {
    return groups.every((group) => group.permissions.refund);
  }

  /* Incoming and outgoing items are removed through different backend paths, so a selection mixing
  the two has no single remove action. */
  function areSelectedRemovable(): boolean {
    const incoming = groups.filter((group) => group.__typename === 'IncomingItemGroup');
    if (incoming.length && incoming.length !== groups.length) {
      return false;
    }

    return groups.every((group) => group.permissions.remove);
  }

  function areSelectedIncoming(): boolean {
    return groups.every((group) => group.__typename === 'IncomingItemGroup');
  }

  function refundNeedsParameters(): boolean {
    return groups.some(
      (group) =>
        group.product?.__typename === 'Event' ||
        group.product?.__typename === 'MerchProduct' ||
        group.product?.__typename === 'ShippingProduct'
    );
  }

  function removalNeedsParameters(): boolean {
    return groups.some((group) => group.__typename === 'OutgoingItemGroup' && group.product?.__typename === 'Event');
  }

  const selectedOutgoingTickets: Ticket[] = compact(
    groups
      .filter((group) => group.__typename === 'OutgoingItemGroup' && group.product?.__typename === 'Event')
      .flatMap((group) => (group as OutgoingItemGroup).tickets)
  );

  if (!groups.length) {
    return (
      <>
        <Icon
          name="plus"
          onClick={() => {
            onSetModal('addProduct');
          }}
        />
      </>
    );
  }

  async function handleOnItemsDelete(): Promise<void> {
    try {
      if (areSelectedIncoming()) {
        await deleteItems(order.id, selection.values);
      } else {
        await removeItems({ orderId: order.id, itemIds: selection.values });
      }
      selection.clear();
      onChange();
    } catch (e) {
      handleError(e);
    }
  }

  async function handleOnProductsRefund(): Promise<void> {
    try {
      await refundItems({
        orderId: order.id,
        itemIds: selection.values,
      });
      selection.clear();
      onChange();
    } catch (e) {
      handleError(e);
    }
  }

  const actions = new Array<JSX.Element>();

  if (areSelectedRefundable()) {
    actions.push(
      <Icon
        key="refund"
        name="rotate-ccw"
        id="refund"
        tooltip={t.model.refund.refundItems}
        onClick={() => {
          if (refundNeedsParameters()) {
            onSetModal('refund');
          } else {
            handleOnProductsRefund();
          }
        }}
      />
    );
  }

  if (areSelectedRemovable()) {
    actions.push(
      <Icon
        key="delete"
        style={{ marginLeft: '16px' }}
        name="trash"
        id="delete"
        onClick={() => {
          if (removalNeedsParameters()) {
            modal.present(
              RemoveItemsModal,
              {
                orderId: order.id,
                itemGroups: groups,
                itemIds: selection.values,
                onChange: () => {
                  selection.clear();
                  onChange();
                },
              },
              { size: 'small' }
            );
          } else {
            handleOnItemsDelete();
          }
        }}
      />
    );
  }

  if (selectedOutgoingTickets.length > 0 && selectedOutgoingTickets.every((e) => e.url)) {
    actions.push(
      <Icon
        key="share"
        name="share"
        id="share"
        tooltip={t.general.sendViaEmail}
        onClick={() => shareTickets(order, selectedOutgoingTickets)}
      />
    );
  }

  const selectedTickets = selection.values
    .map((id) => selectedOutgoingTickets.find((ticket) => ticket.id === id))
    .filter(Boolean) as Ticket[];

  if (selectedTickets.length > 0 && selectedTickets.every((ticket) => ticket.permissions.offerForResale)) {
    const isBankInformationRequired = selectedTickets.some(
      (ticket) => ticket.resaleAvailability === 'available_with_transfer'
    );

    actions.push(
      <Icon
        key="resale"
        name="resale"
        id="resale"
        tooltip={t.model.order.offerForResale.title}
        onClick={() =>
          modal.present(
            OfferForResale,
            {
              id: order.id,
              itemIds: selection.values,
              isBankInformationRequired,
              onSuccess: async () => {
                try {
                  await loadOrderWithItemGroups(order.id);
                  selection.clear();
                  onChange();
                } catch (e) {
                  handleError(e);
                }
              },
            },
            { size: 'small' }
          )
        }
      />
    );
  }

  return (
    <div className="order-items__actions">
      {actions}
      {actions.length === 0 && <div>{t.errors.noPossibleActions}</div>}
    </div>
  );
}
